import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { admAPI } from "../../lib/admApi";
import { toast } from "sonner";
import {
  School, ChevronRight, CheckCircle2, Clock, Trophy, CalendarClock,
  MessageSquare, FileText, User, ArrowRight, Sparkles,
} from "lucide-react";

/**
 * Applicant Portal — the secure, token-based view for applicants & families
 * once an application has been submitted. Path:
 *   /education360/applicant/:school?token=<application_token>
 * Falls back to reading token from localStorage if not in URL.
 */
export default function ApplicantPortal() {
  const { school } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const tokenFromUrl = params.get("token");
  const token = tokenFromUrl || localStorage.getItem(`edu360.applicant.${school}.token`);

  const load = async () => {
    if (!token) return setData({ noToken: true });
    try {
      const { data } = await admAPI.loadApplicantPortal(token);
      setData(data);
      localStorage.setItem(`edu360.applicant.${school}.token`, token);
    } catch { setData({ noToken: true }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const accept = async (oid) => {
    try { await admAPI.acceptOffer(token, oid); toast.success("Offer accepted"); load(); }
    catch { toast.error("Failed"); }
  };
  const decline = async (oid) => {
    if (!confirm("Decline this offer? This cannot be undone.")) return;
    try { await admAPI.declineOffer(token, oid); toast.success("Offer declined"); load(); }
    catch { toast.error("Failed"); }
  };

  if (!data) return <div className="edu360-root p-10 text-slate-500">Loading applicant portal…</div>;
  if (data.noToken) {
    return (
      <div className="edu360-root min-h-screen bg-[color:var(--hl-page)] grid place-items-center p-6">
        <div className="hl-card p-8 max-w-md text-center">
          <School className="w-8 h-8 mx-auto text-[color:var(--hl-navy)]" />
          <h2 className="font-display font-bold text-xl mt-3">Applicant portal</h2>
          <p className="text-slate-500 mt-2 text-sm">Please open the secure link included in your confirmation email, or start a new application.</p>
          <Link to={`/education360/apply/${school}`} className="hl-button-primary text-xs mt-4 inline-block">Start an application</Link>
        </div>
      </div>
    );
  }

  const a = data.applicant;
  const tenant = data.tenant || {};
  const c = data.completeness || {};
  const openOffer = (data.offers || []).find(o => o.status === "issued");
  const openInterview = (data.interviews || []).find(i => i.status === "scheduled");
  const openAssessment = (data.assessments || []).find(a => a.status === "scheduled");

  const checklist = [
    { done: !!a.first_name && !!a.last_name, label: "Applicant details" },
    { done: (a.parents || []).some(p => p.email), label: "Parent contact" },
    { done: (a.previous_schools || []).length > 0, label: "Previous school history" },
    { done: (a.references || []).length > 0, label: "Reference" },
    { done: (a.documents || []).length >= 3, label: "Supporting documents (3+)" },
    { done: !!a.essay, label: "Personal statement" },
    { done: !!a.declaration_accepted, label: "Declaration signed" },
  ];

  return (
    <div className="edu360-root min-h-screen bg-[color:var(--hl-page)]" data-testid="applicant-portal">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <School className="w-5 h-5 text-[color:var(--hl-navy)]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold">Applicant Portal</div>
              <div className="font-display font-bold text-slate-800">{tenant.name || "Highlands International College"}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Application</div>
            <div className="mono text-slate-800 font-semibold">{a.application_no}</div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: status + checklist */}
        <div className="lg:col-span-2 space-y-6">
          <div className="hl-card p-6" data-testid="status-panel">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold">Current stage</div>
            <div className="font-display font-bold text-2xl text-[color:var(--hl-navy)] capitalize mt-1">
              {(a.stage || "").replace(/_/g, " ")}
            </div>
            <div className="text-sm text-slate-500 mt-2">
              Welcome, {a.first_name}. Your application for <b>{a.year_group_applied}</b> ({a.entry_year}) is progressing.
            </div>

            {openOffer && (
              <div className="mt-6 border-2 border-[color:var(--hl-orange)] rounded-md p-5 bg-[color:var(--hl-orange)]/5" data-testid="offer-panel">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[color:var(--hl-orange)]" />
                  <div className="font-display font-bold text-lg text-slate-800">An offer has been issued</div>
                </div>
                <div className="text-sm text-slate-700 mt-2">
                  <b>{openOffer.offer_type}</b> offer for {openOffer.year_group}
                  {openOffer.scholarship_pct ? ` · Scholarship ${openOffer.scholarship_pct}%` : ""}.
                  Annual total <b>${(openOffer.fee_summary?.annual_total || 0).toLocaleString()}</b>.
                  Please respond by <b>{openOffer.acceptance_deadline}</b>.
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => accept(openOffer.id)} className="hl-button-primary text-xs" data-testid="btn-accept-offer">
                    Accept offer <ArrowRight className="w-3 h-3 inline ml-1" />
                  </button>
                  <button onClick={() => decline(openOffer.id)} className="hl-button-secondary text-xs" data-testid="btn-decline-offer">Decline</button>
                </div>
              </div>
            )}

            {openInterview && (
              <div className="mt-4 hl-card p-4 border-l-4 border-l-[color:var(--hl-navy)]" data-testid="interview-panel">
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-[color:var(--hl-navy)]" />
                  <div className="font-semibold">Interview scheduled</div>
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  <b>{(openInterview.scheduled_at || "").replace("T", " ").slice(0, 16)}</b> · {openInterview.virtual ? "Virtual meeting" : openInterview.location}
                </div>
              </div>
            )}

            {openAssessment && (
              <div className="mt-3 hl-card p-4 border-l-4 border-l-[color:var(--hl-navy)]" data-testid="assessment-panel">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[color:var(--hl-navy)]" />
                  <div className="font-semibold">Assessment scheduled</div>
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  {openAssessment.type} {openAssessment.subject && `· ${openAssessment.subject}`} — <b>{(openAssessment.scheduled_at || "").replace("T", " ").slice(0, 16)}</b> · {openAssessment.location}
                </div>
              </div>
            )}
          </div>

          <div className="hl-card p-6" data-testid="checklist-panel">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold">Checklist</div>
            <div className="font-display font-bold text-xl mt-1">Application completeness — {c.score || 0}%</div>
            <div className="mt-4 space-y-2 text-sm">
              {checklist.map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                  {it.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-amber-500" />}
                  <span className={it.done ? "text-slate-700" : "text-amber-700"}>{it.label}</span>
                </div>
              ))}
            </div>
            {a.stage === "application_draft" && (
              <Link to={`/education360/apply/${school}`} className="hl-button-primary text-xs mt-4 inline-block" data-testid="btn-continue-app">
                Continue application
              </Link>
            )}
          </div>

          <div className="hl-card p-6" data-testid="messages-panel">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-slate-500" />
              <div className="font-display font-bold text-lg">Messages from the school</div>
            </div>
            {(data.messages || []).length === 0 && <div className="text-slate-500 text-sm">No messages yet.</div>}
            <div className="space-y-3">
              {(data.messages || []).map(m => (
                <div key={m.id} className="border-l-2 border-slate-200 pl-3">
                  <div className="text-xs text-slate-500">{(m.sent_at || "").replace("T", " ").slice(0, 16)}</div>
                  <div className="font-semibold text-slate-800">{m.subject}</div>
                  <div className="text-sm text-slate-600 whitespace-pre-wrap mt-1">{m.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column: applicant summary */}
        <div className="space-y-4">
          <div className="hl-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Applicant</div>
            <div className="font-display font-bold text-xl mt-1">{a.first_name} {a.last_name}</div>
            <div className="text-xs text-slate-500 mt-1">{a.nationality || "—"} · {a.date_of_birth || "—"}</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <Info label="Year" v={a.year_group_applied} />
              <Info label="Entry" v={`${a.entry_year} · ${a.entry_term}`} />
              <Info label="Boarding" v={a.boarding_requested ? "Yes" : "No"} />
              <Info label="Scholarship" v={a.scholarship_requested ? a.scholarship_category || "Yes" : "No"} />
            </div>
          </div>

          <div className="hl-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Documents</div>
            <div className="mt-2 text-sm space-y-1">
              {(a.documents || []).map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  <span className="flex-1 truncate">{d.name}</span>
                  {d.verified ? <span className="text-emerald-600 text-[10px]">✓</span> : <span className="text-amber-600 text-[10px]">Pending</span>}
                </div>
              ))}
              {(a.documents || []).length === 0 && <div className="text-slate-500">No documents uploaded.</div>}
            </div>
          </div>

          <div className="text-[11px] text-slate-400 text-center">
            Powered by Education360™ · Admissions360
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, v }) {
  return (
    <div>
      <div className="text-slate-500 uppercase tracking-widest text-[10px]">{label}</div>
      <div className="text-slate-800 font-medium">{v || "—"}</div>
    </div>
  );
}
